import { QuotaResponse } from 'types/billing';

const CYCLE = {
  start: '2026-05-01T00:00:00.000Z',
  end: '2026-05-31T00:00:00.000Z',
};

export const QUOTA_FIXTURES = {
  'trial-first-day': {
    trial: { active: true, days_remaining: 14, day_number: 1, total_days: 15 },
    plan: { name: 'Trial', credits_granted: 0, credits_remaining: 0 },
    overage: null,
    blocked: null,
    cycle: CYCLE,
    usage_pct: 2,
  } satisfies QuotaResponse,

  'trial-early': {
    trial: { active: true, days_remaining: 12, day_number: 3, total_days: 15 },
    plan: { name: 'Trial', credits_granted: 0, credits_remaining: 0 },
    overage: null,
    blocked: null,
    cycle: CYCLE,
    usage_pct: 20,
  } satisfies QuotaResponse,

  'trial-ending': {
    trial: { active: true, days_remaining: 2, day_number: 13, total_days: 15 },
    plan: { name: 'Trial', credits_granted: 0, credits_remaining: 0 },
    overage: null,
    blocked: null,
    cycle: CYCLE,
    usage_pct: 45,
  } satisfies QuotaResponse,

  // Final day — renders the `ending` band reading "1 day left" (days-only).
  'trial-last-day': {
    trial: { active: true, days_remaining: 1, day_number: 14, total_days: 15 },
    plan: { name: 'Trial', credits_granted: 0, credits_remaining: 0 },
    overage: null,
    blocked: null,
    cycle: CYCLE,
    usage_pct: 62,
  } satisfies QuotaResponse,

  healthy: {
    trial: null,
    plan: { name: 'Starter', credits_granted: 10000, credits_remaining: 6200 },
    overage: null,
    blocked: null,
    cycle: CYCLE,
    usage_pct: 38,
  } satisfies QuotaResponse,

  approaching: {
    trial: null,
    plan: { name: 'Starter', credits_granted: 10000, credits_remaining: 850 },
    overage: null,
    blocked: null,
    cycle: CYCLE,
    usage_pct: 91,
  } satisfies QuotaResponse,

  overage: {
    trial: null,
    plan: { name: 'Starter', credits_granted: 10000, credits_remaining: 0 },
    overage: { active: true, credits: 3200, cap: 10000, used: 3200 },
    blocked: null,
    cycle: CYCLE,
    usage_pct: 132,
  } satisfies QuotaResponse,

  // Hard-block states — one per BlockedReason (PLT-4074). `blocked` keeps its
  // historical meaning (overage-cap-reached); the others are explicit.
  blocked: {
    trial: null,
    plan: { name: 'Starter', credits_granted: 10000, credits_remaining: 0 },
    overage: { active: true, credits: 10000, cap: 10000, used: 10000 },
    blocked: { reason: 'overage-cap-reached' },
    cycle: CYCLE,
    usage_pct: 200,
  } satisfies QuotaResponse,

  'blocked-trial-end': {
    trial: null,
    plan: null,
    overage: null,
    blocked: { reason: 'trial-end' },
    cycle: CYCLE,
    usage_pct: 100,
  } satisfies QuotaResponse,

  // Trial date lapsed but the BE has NOT yet set blocked:trial-end (QAT-388
  // window). No paid plan → the FE sidebar safety net should still surface the
  // "Trial ended" widget off accountInfo.trial.active=false (PLT-4196).
  'trial-lapsed': {
    trial: null,
    plan: null,
    overage: null,
    blocked: null,
    cycle: CYCLE,
    usage_pct: 100,
  } satisfies QuotaResponse,

  'blocked-payment-failed': {
    trial: null,
    plan: { name: 'Starter', credits_granted: 10000, credits_remaining: 4200 },
    overage: null,
    blocked: { reason: 'payment-failed' },
    cycle: CYCLE,
    usage_pct: 58,
  } satisfies QuotaResponse,

  'blocked-subscription-canceled': {
    trial: null,
    plan: { name: 'Starter', credits_granted: 10000, credits_remaining: 0 },
    overage: null,
    blocked: { reason: 'subscription-canceled' },
    cycle: CYCLE,
    usage_pct: 100,
  } satisfies QuotaResponse,

  // Migrated free user — trial just started, welcome popup not yet seen
  'trial-migrated-free': {
    trial: { active: true, days_remaining: 14, day_number: 1, total_days: 14 },
    plan: { name: 'Trial', credits_granted: 0, credits_remaining: 0 },
    overage: null,
    blocked: null,
    cycle: CYCLE,
    usage_pct: 0,
  } satisfies QuotaResponse,

  // Migrated free user who had an annual prepaid plan — shows refund note
  'trial-migrated-free-annual': {
    trial: { active: true, days_remaining: 14, day_number: 1, total_days: 14 },
    plan: { name: 'Trial', credits_granted: 0, credits_remaining: 0 },
    overage: null,
    blocked: null,
    cycle: CYCLE,
    usage_pct: 0,
  } satisfies QuotaResponse,

  // Migrated free user whose trial has lapsed (active=false) — not yet hard-blocked.
  // Drives TrialBannerEnded on the billing page.
  'trial-migrated-free-lapsed': {
    trial: { active: false, days_remaining: 0, day_number: 15, total_days: 14 },
    plan: { name: 'Trial', credits_granted: 0, credits_remaining: 0 },
    overage: null,
    blocked: null,
    cycle: CYCLE,
    usage_pct: 100,
  } satisfies QuotaResponse,
} as const;

export type QaBillingState = keyof typeof QUOTA_FIXTURES;

export const QA_BILLING_STATE_PARAM = 'qa_billing_state';

/**
 * Returns the qa_billing_state value from the current URL search params.
 * Undefined when the param is absent or unrecognised.
 */
export const getQaBillingState = (): QaBillingState | undefined => {
  const raw = new URLSearchParams(window.location.search).get(
    QA_BILLING_STATE_PARAM,
  );

  return raw !== null && raw in QUOTA_FIXTURES
    ? (raw as QaBillingState)
    : undefined;
};
